import React, { useEffect, useRef, useState } from "react";
import { api, API, fmtDate, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, KeyRound, Lock, Signature, Upload, Trash2, ShieldCheck } from "lucide-react";
import { SearchBox, DeleteButton, matchesQuery } from "@/components/crm/RecordActions";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const empty = { name:"", email:"", mobile:"", password:"", role:"sales" };

const MODULES = [
  { key: "leads", label: "Leads", hint: "Lead pipeline & follow-ups" },
  { key: "customers", label: "Customers", hint: "Customer records" },
  { key: "quotations", label: "Quotations", hint: "5-page proposals + PDF" },
  { key: "tax_invoices", label: "Tax Invoices", hint: "GST invoices" },
  { key: "payments", label: "Payments", hint: "Customer receipts & vendor payments" },
  { key: "mrn", label: "Material Requisition", hint: "Approved supplier MR notes" },
  { key: "projects", label: "Projects", hint: "Site work, photos, KYC" },
  { key: "wcr", label: "Work Completion", hint: "DESCOM completion reports" },
  { key: "services", label: "Services", hint: "AMC & repair tickets" },
  { key: "complaints", label: "Complaints", hint: "Customer complaints" },
  { key: "reports", label: "Reports", hint: "Analytics dashboards" },
  { key: "users", label: "Users", hint: "Manage other users" },
  { key: "settings", label: "Settings & Firms", hint: "Firms, bank, logo" },
  { key: "integrations", label: "Integrations", hint: "Meta / Webhooks" },
];

// Default permissions per role (used when user has no explicit permissions map yet).
const DEFAULTS = {
  admin:      { leads:true, customers:true, quotations:true, tax_invoices:true, payments:true, mrn:true, projects:true, wcr:true, services:true, complaints:true, reports:true, users:true, settings:true, integrations:true },
  sales:      { leads:true, customers:true, quotations:true, tax_invoices:false, payments:true, mrn:true, projects:false, wcr:false, services:false, complaints:false, reports:false, users:false, settings:false, integrations:false },
  technician: { leads:false, customers:false, quotations:false, tax_invoices:false, payments:false, mrn:false, projects:false, wcr:false, services:true, complaints:true, reports:false, users:false, settings:false, integrations:false },
};

export default function Users() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [sigTarget, setSigTarget] = useState(null);
  const [sigBusy, setSigBusy] = useState(false);
  const [permTarget, setPermTarget] = useState(null);
  const [permMap, setPermMap] = useState({});
  const sigInputRef = useRef(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("mrm_token") : null;

  const load = async () => setItems((await api.get("/users")).data);
  useEffect(()=>{ load(); },[]);

  const save = async () => {
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false); setForm(empty); load();
    } catch(e){ toast.error(formatError(e.response?.data?.detail)); }
  };

  const del = async (u) => {
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); }
    catch(e){ toast.error(formatError(e.response?.data?.detail)); }
  };

  const doReset = async () => {
    if (!resetPw || resetPw.length < 6) { toast.error("Password must be 6+ chars"); return; }
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: resetPw });
      toast.success(`Password reset for ${resetTarget.email}`);
      setResetTarget(null); setResetPw("");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const uploadSig = async (file) => {
    if (!file) return;
    setSigBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch(`/users/${sigTarget.id}`, { signature_url: data.path });
      toast.success(`Signature saved for ${sigTarget.name}`);
      const fresh = (await api.get("/users")).data.find(u => u.id === sigTarget.id);
      setSigTarget(fresh || null);
      setItems((await api.get("/users")).data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSigBusy(false); if (sigInputRef.current) sigInputRef.current.value = ""; }
  };

  const removeSig = async () => {
    try {
      await api.patch(`/users/${sigTarget.id}`, { signature_url: "" });
      toast.success(`Signature removed for ${sigTarget.name}`);
      const fresh = (await api.get("/users")).data.find(u => u.id === sigTarget.id);
      setSigTarget(fresh || null);
      setItems((await api.get("/users")).data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const openPerms = (u) => {
    setPermTarget(u);
    setPermMap({ ...(DEFAULTS[u.role] || {}), ...(u.permissions || {}) });
  };

  const savePerms = async () => {
    try {
      await api.patch(`/users/${permTarget.id}`, { permissions: permMap });
      toast.success(`Access updated for ${permTarget.name}`);
      setPermTarget(null); setPermMap({});
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const resetToRoleDefaults = () => {
    if (!permTarget) return;
    setPermMap({ ...(DEFAULTS[permTarget.role] || {}) });
    toast("Restored role defaults");
  };

  const visible = items.filter((u) => matchesQuery(u, search));

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Outfit'}}>Users & Permissions</h1>
          <p className="text-slate-500 text-sm">Access control, signatures & password resets. Only Admin is permanent — all others can be deleted or role-changed.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SearchBox value={search} onChange={setSearch} placeholder="Search name, email..." testid="users-search-input" />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-user-btn"><Plus className="w-4 h-4 mr-1"/>New User</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New User</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="user-name-input"/></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="user-email-input"/></div>
                <div><Label>Mobile</Label><Input type="tel" placeholder="10-digit mobile" value={form.mobile} onChange={e=>setForm({...form, mobile:e.target.value})} data-testid="user-mobile-input"/></div>
                <div><Label>Password</Label><Input type="password" value={form.password} onChange={e=>setForm({...form, password:e.target.value})} data-testid="user-password-input"/></div>
                <div><Label>Role</Label>
                  <Select value={form.role} onValueChange={v=>setForm({...form, role:v})}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="sales">Sales</SelectItem>
                      <SelectItem value="technician">Technician</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button className="bg-amber-500 hover:bg-amber-600" onClick={save} data-testid="save-user-btn">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Mobile</TableHead><TableHead>Role</TableHead><TableHead>Signature</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {visible.length===0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">{search ? `No users matching "${search}"` : "No users."}</TableCell></TableRow>}
            {visible.map(u=>(
              <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                <TableCell className="font-medium">
                  {u.name}
                  {u.seeded && <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] text-slate-500"><Lock className="w-2.5 h-2.5"/>Permanent</span>}
                </TableCell>
                <TableCell className="text-sm">{u.email}</TableCell>
                <TableCell className="text-sm font-mono">{u.mobile || "-"}</TableCell>
                <TableCell><span className="text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 uppercase font-semibold">{u.role}</span></TableCell>
                <TableCell>
                  {u.signature_url ? (
                    <img src={`${API}/files/${u.signature_url}?auth=${token}`} alt="sig" className="h-8 max-w-[110px] object-contain border border-slate-200 rounded bg-white" data-testid={`sig-thumb-${u.email}`}/>
                  ) : <span className="text-xs text-slate-400">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1 flex-wrap">
                    <Button size="sm" variant="outline" onClick={()=>openPerms(u)} data-testid={`access-btn-${u.email}`} className="border-blue-200 text-blue-800 hover:bg-blue-50">
                      <ShieldCheck className="w-3.5 h-3.5 mr-1"/>Access
                    </Button>
                    <Button size="sm" variant="outline" onClick={()=>setSigTarget(u)} data-testid={`signature-btn-${u.email}`}>
                      <Signature className="w-3.5 h-3.5 mr-1"/>Signature
                    </Button>
                    <Button size="sm" variant="outline" onClick={()=>{setResetTarget(u); setResetPw("");}} data-testid={`reset-password-${u.email}`}>
                      <KeyRound className="w-3.5 h-3.5 mr-1"/>Password
                    </Button>
                    {u.id !== user.id && !u.seeded && (
                      <DeleteButton title={`Delete user ${u.name}?`} description={`This will permanently remove ${u.email} (${u.role}) access.`} onConfirm={()=>del(u)} testid={`delete-user-${u.email}`} />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {/* Password reset dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(v)=>{ if(!v) setResetTarget(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password for {resetTarget?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-2">
              Admin-only action. User will use this new password on next login.
            </div>
            <div><Label>New Password</Label><Input type="password" value={resetPw} onChange={e=>setResetPw(e.target.value)} placeholder="Min 6 chars" data-testid="reset-new-password-input" autoFocus /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setResetTarget(null)}>Cancel</Button>
            <Button className="bg-amber-500 hover:bg-amber-600" onClick={doReset} data-testid="confirm-reset-password-btn">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature dialog */}
      <Dialog open={!!sigTarget} onOpenChange={(v)=>{ if(!v) { setSigTarget(null); if (sigInputRef.current) sigInputRef.current.value = ""; }}}>
        <DialogContent>
          <DialogHeader><DialogTitle>Digital Signature — {sigTarget?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-slate-500 bg-blue-50 border border-blue-200 rounded-lg p-2">
              Upload a transparent PNG signature (ideally 300×100 px). It will be auto-appended to every Quotation and Tax Invoice that this user creates.
            </div>
            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/60 min-h-[120px] flex items-center justify-center">
              {sigTarget?.signature_url ? (
                <img src={`${API}/files/${sigTarget.signature_url}?auth=${token}`} alt="Current signature" className="max-h-24 object-contain" data-testid="signature-preview"/>
              ) : <span className="text-sm text-slate-400">No signature yet</span>}
            </div>
            <input ref={sigInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={(e) => uploadSig(e.target.files?.[0])} data-testid="signature-file-input"/>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" onClick={()=>sigInputRef.current?.click()} disabled={sigBusy}
                className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="upload-signature-btn">
                <Upload className="w-4 h-4 mr-1"/>{sigBusy ? "Uploading..." : (sigTarget?.signature_url ? "Replace Signature" : "Upload Signature")}
              </Button>
              {sigTarget?.signature_url && (
                <Button type="button" variant="outline" onClick={removeSig} disabled={sigBusy}
                  className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="remove-signature-btn">
                  <Trash2 className="w-4 h-4 mr-1"/>Remove
                </Button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setSigTarget(null)} data-testid="close-signature-dialog">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access & Permissions dialog */}
      <Dialog open={!!permTarget} onOpenChange={(v)=>{ if(!v){setPermTarget(null); setPermMap({});} }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-blue-600"/>Access Control — {permTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500 bg-blue-50 border border-blue-200 rounded-lg p-2">
              Tick the modules {permTarget?.name} can access. Nav sidebar updates immediately on next login. Role: <span className="font-semibold uppercase">{permTarget?.role}</span>.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {MODULES.map(m => (
                <label key={m.key} className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition ${permMap[m.key] ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200 hover:bg-slate-50"}`}
                  data-testid={`perm-${m.key}`}>
                  <input type="checkbox" checked={!!permMap[m.key]} onChange={(e)=>setPermMap({...permMap, [m.key]:e.target.checked})} className="mt-0.5" data-testid={`perm-check-${m.key}`}/>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-900">{m.label}</div>
                    <div className="text-[11px] text-slate-500">{m.hint}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter className="flex justify-between">
            <Button variant="outline" onClick={resetToRoleDefaults} data-testid="perm-reset-defaults">Reset to role defaults</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={()=>{setPermTarget(null); setPermMap({});}}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={savePerms} data-testid="save-perms-btn">Save Access</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
